import type { OnboardingAddMethod } from '#~/analytics/constants/onboarding-events.js';
import { EVENT_CATEGORIES, EVENT_NAMES, EVENT_SEVERITY } from '#~/analytics/constants/index.js';
import type { EventProperties } from '#~/analytics/types/properties.js';
import { resolveLinkingSurface } from './linking-surface.js';
import type { AnalyticsTrackFn } from './types.js';

// Per ENG-3960 §E12 — canonical device cohort shape shared across all wizard/control events.
// IDs power PostHog cohort breakdowns; *_name props supply human-readable labels.
// device_brand/device_model/brand/model/brandId/modelId are OFF-CONVENTION and rejected by
// assertCanonicalDeviceProps (throws in dev/test, drops in prod).
interface WizardDeviceProperties extends EventProperties {
  /** Entry choice is known by the wizard, not inferred by shared producers. */
  add_method?: OnboardingAddMethod;
  device_brand_id?: string;
  brand_name?: string;
  device_model_id?: string;
  model_name?: string;
}

interface DeviceAddSuccessProperties extends WizardDeviceProperties {
  device_id: string;
  deviceType?: string;
  studio_id?: string;
  bitrate?: number;
  resolution?: string;
  fps?: number;
  // Additive (proposal §5a-2): the One-Click vs manual add mode + where the model
  // id came from. Declared on the canonical device_add property bag. Optional so
  // every existing call site keeps compiling. Not an off-convention device key, so
  // the assertCanonicalDeviceProps gate (ENG-3960 §E12) leaves it untouched.
  add_mode?: 'one_click' | 'manual';
  model_id_source?: 'backend' | 'manual_picker';
}

// ENG-4323 — properties for the add-camera FAILURE event. device_id is REQUIRED
// (not optional like the wizard-step events): it is half the join key to the
// ENG-4304 device_add_failed $exception Issue (session_id + device_id), so a
// failure event without it cannot be correlated to its exception. error_type /
// error_message describe what failed; the brand/model cohort props come from
// WizardDeviceProperties for the same PostHog breakdowns the success event uses.
interface DeviceAddFailedProperties extends WizardDeviceProperties {
  device_id: string;
  studio_id?: string;
  error_type?: string;
  error_message?: string;
}

// Canonical selection properties — removed device_brand/device_model (off-convention per ENG-3960 §E12).
interface DeviceSelectionProperties extends WizardDeviceProperties {
  device_id?: string;
  studio_id?: string;
}

interface DeviceClickDiscoveredProperties extends WizardDeviceProperties {
  deviceId?: string;
  deviceType?: string;
  ip?: string;
  studio_id?: string;
}

interface DeviceContinueToVideoProperties extends WizardDeviceProperties {
  deviceId?: string;
  sourceType?: string;
  studio_id?: string;
  // Additive (proposal §5a-1): One-Click vs manual discriminator on the continue
  // step. Optional so existing callers are unaffected.
  add_mode?: 'one_click' | 'manual';
}

interface DeviceContinueFromVideoProperties extends WizardDeviceProperties {
  deviceId?: string;
  studio_id?: string;
}

interface DeviceContinueFromBrandProperties extends WizardDeviceProperties {
  deviceId?: string;
  studio_id?: string;
}

interface EditControlSettingsProperties extends WizardDeviceProperties {
  deviceId?: string;
  controlLabel?: string;
  controlKey?: string;
  controlValue?: unknown;
}

interface EditAdvancedControlSettingsProperties extends WizardDeviceProperties {
  controlSettingKey?: string;
  controlSettingValue?: unknown;
}

interface SelectControlTypeProperties extends WizardDeviceProperties {
  controlType?: string;
}

interface SelectNoControlProperties extends WizardDeviceProperties {
  controlType?: string;
}

interface AddGamepadControllerProperties extends WizardDeviceProperties {
  controllerType?: string;
  controllerName?: string;
  controllerId?: string;
}

interface PressAddIpControllerProperties extends WizardDeviceProperties {}

interface SuccessfullyAddIpControllerProperties extends WizardDeviceProperties {
  controllerType?: string;
  ipAddress?: string;
  port?: number;
}

// Adds backwards-compat non-cohort aliases (deviceId ← device_id, studioId ← studio_id).
// Per ENG-3960 §E12: brand/model aliases are OFF-CONVENTION and MUST NOT be added here —
// they would trigger assertCanonicalDeviceProps in dev/test.
const withDeviceAliases = <T extends EventProperties>(properties: T): T => {
  return {
    ...properties,
    ...(properties.device_id ? { deviceId: properties.device_id } : {}),
    ...(properties.studio_id ? { studioId: properties.studio_id } : {})
  };
};

// Permanent activation is device_add; failed attempts use device_add_failed.
export const createDeviceLifecycleTracker = (track: AnalyticsTrackFn) => ({
  trackDeviceAddSuccess: (properties: DeviceAddSuccessProperties): void => {
    // device_add's canonical-event-map row REQUIRES is_embedded + linking_surface,
    // and expectCanonicalEvent enforces the required set — so both have to be on
    // EVERY add, not only embedded ones. The wizard previously spread
    // resolveEmbeddedFlowProps(), which yields {} for a generic add, leaving the
    // canonical activation event without two of its own required properties while
    // the other two producers (embedded-device-flow.ts, agent/broker.ts) always
    // stamp them.
    //
    // Defaults, not overwrites: the embedded wizard path spreads
    // is_embedded: true over these, and that must win.
    //
    // is_embedded: false rather than absent because absent and false were
    // indistinguishable downstream — a PostHog breakdown on is_embedded silently
    // dropped every generic add instead of bucketing it.
    const canonicalProperties = withDeviceAliases({
      is_embedded: false,
      linking_surface: resolveLinkingSurface(),
      ...properties
    });

    track(EVENT_NAMES.DEVICE_ADD, EVENT_CATEGORIES.DEVICE, canonicalProperties);
  },

  // ENG-4323 — the queryable add-camera failure event. Single-emit (no legacy
  // dual-write): today an add failure exists only as a raw $exception the funnel
  // cannot count, so this custom event makes it queryable and pairs with the
  // ENG-4304 device_add_failed $exception Issue (join: session_id + device_id).
  // severity S2/error is stamped HERE, not at the call site, so every emission is
  // classified identically for blocking-spike alerts (ENG-4340 severity taxonomy;
  // rule: never-use-type-assertions — the typed EVENT_SEVERITY const, no literal).
  trackDeviceAddFailed: (properties: DeviceAddFailedProperties): void => {
    track(EVENT_NAMES.DEVICE_ADD_FAILED, EVENT_CATEGORIES.DEVICE, {
      ...withDeviceAliases(properties),
      severity: EVENT_SEVERITY.S2_ERROR
    });
  },

  trackDeviceSelectBrand: (properties: DeviceSelectionProperties): void => {
    track(EVENT_NAMES.DEVICE_SELECT_BRAND, EVENT_CATEGORIES.DEVICE, withDeviceAliases(properties));
  },

  trackDeviceSelectModel: (properties: DeviceSelectionProperties): void => {
    track(EVENT_NAMES.DEVICE_SELECT_MODEL, EVENT_CATEGORIES.DEVICE, withDeviceAliases(properties));
  },

  // Per ENG-3960 §F — every device-scoped wizard event carries device_model_id + device_brand_id
  // so PostHog cohort breakdowns work structurally rather than per-call-site.

  trackDeviceClickDiscovered: (properties: DeviceClickDiscoveredProperties): void => {
    track(EVENT_NAMES.DEVICE_CLICK_DISCOVERED, EVENT_CATEGORIES.DEVICE, properties);
  },

  trackDeviceContinueToVideo: (properties: DeviceContinueToVideoProperties): void => {
    track(EVENT_NAMES.DEVICE_CONTINUE_TO_VIDEO, EVENT_CATEGORIES.DEVICE, properties);
  },

  trackDeviceContinueFromVideo: (properties: DeviceContinueFromVideoProperties): void => {
    track(EVENT_NAMES.DEVICE_CONTINUE_FROM_VIDEO, EVENT_CATEGORIES.DEVICE, properties);
  },

  trackDeviceContinueFromBrand: (properties: DeviceContinueFromBrandProperties): void => {
    track(EVENT_NAMES.DEVICE_CONTINUE_FROM_BRAND, EVENT_CATEGORIES.DEVICE, properties);
  },

  trackEditControlSettings: (properties: EditControlSettingsProperties): void => {
    track(EVENT_NAMES.EDIT_CONTROL_SETTINGS, EVENT_CATEGORIES.CONTROL_TRAY, properties);
  },

  trackEditAdvancedControlSettings: (properties: EditAdvancedControlSettingsProperties): void => {
    track(
      EVENT_NAMES.EDIT_ADVANCED_CONTROL_SETTINGS,
      EVENT_CATEGORIES.ADVANCED_SETTINGS,
      properties
    );
  },

  trackSelectControlType: (properties: SelectControlTypeProperties): void => {
    track(EVENT_NAMES.SELECT_CONTROL_TYPE, EVENT_CATEGORIES.VIDEO_SOURCES, properties);
  },

  trackSelectNoControl: (properties: SelectNoControlProperties): void => {
    track(EVENT_NAMES.SELECT_NO_CONTROL, EVENT_CATEGORIES.VIDEO_SOURCES, properties);
  },

  trackAddGamepadController: (properties: AddGamepadControllerProperties): void => {
    track(EVENT_NAMES.ADD_GAMEPAD_CONTROLLER, EVENT_CATEGORIES.CONTROLLERS, properties);
  },

  trackPressAddIpController: (properties?: PressAddIpControllerProperties): void => {
    track(EVENT_NAMES.PRESS_ADD_IP_CONTROLLER, EVENT_CATEGORIES.CONTROLLERS, properties);
  },

  trackSuccessfullyAddIpController: (properties: SuccessfullyAddIpControllerProperties): void => {
    track(EVENT_NAMES.SUCCESSFULLY_ADD_IP_CONTROLLER, EVENT_CATEGORIES.CONTROLLERS, properties);
  }
});
